/**
 * CapabilityContext — the frontend half of the Discipline Capability system.
 *
 * Fetches the current expert's capability profile once from the backend
 * (GET /profile/capabilities → { capabilities: { features, content, isImmigration, ... } })
 * and exposes it app-wide. Every immigration-specific surface (IRCC forms, programs &
 * pipelines, firm standards, portal autofill, …) gates its visibility on a feature
 * flag from here, so non-immigration experts never see immigration tools.
 *
 * Usage:
 *   const { features, isImmigration, loading } = useCapabilities();
 *   {features.irccForms && <IRCCStuff/>}          // gate a surface
 *   const showForms = useFeature('irccForms');    // convenience
 *   const { assessmentTemplates } = useWidgetConfig();  // what the widget offers
 *
 * Default while loading / on error: every feature OFF (hidden). This means an
 * immigration expert may see their specialized tools appear a beat after load, but a
 * non-immigration expert NEVER flashes immigration tools — the safe direction.
 */

import React, { createContext, useContext, useEffect, useState } from 'react';
import useAuthenticatedAxios from '../auth/AuthenticatedAxios';

// Mirrors server DEFAULT_FEATURES (data/disciplineCapabilities.ts). Everything off.
const DEFAULT_FEATURES = {
  irccForms: false,
  portalAutofill: false,
  immigrationPrograms: false,
  pathwayDiscovery: false,
  applicationReview: false,
  firmStandards: false,
  irccRequirementsCatalog: false,
  expirationTracker: false,
};

const DEFAULT_CONTENT = {
  domainName: 'advisory services',
  adviserCategoryLabel: 'Professional Advisory',
  expertiseAreas: [],
  toolsetLabel: 'Specialized tools',
};

/**
 * What the expert's public chat widget actually offers, resolved server-side from
 * their discipline AND the CRM's per-category widget settings. Distinct from
 * `features`: those gate dashboard tools, this describes the widget. Empty templates
 * means the widget runs no self-serve questionnaires, so the dashboard should not show
 * an assessments panel that can never fill.
 */
const DEFAULT_WIDGET_CONFIG = {
  discipline: 'default',
  assessmentTemplates: [],
  showAssessmentChoice: false,
  allowAssessmentEntryLinks: false,
};

const DEFAULT_VALUE = {
  loading: true,
  isImmigration: false,
  features: DEFAULT_FEATURES,
  content: DEFAULT_CONTENT,
  widgetConfig: DEFAULT_WIDGET_CONFIG,
  refresh: () => {},
};

const CapabilityContext = createContext(DEFAULT_VALUE);

export const CapabilityProvider = ({ children }) => {
  const apiClient = useAuthenticatedAxios();
  const [state, setState] = useState(DEFAULT_VALUE);

  const load = React.useCallback(async () => {
    try {
      const res = await apiClient.get('/profile/capabilities');
      const cap = res?.data?.capabilities || {};
      setState({
        loading: false,
        isImmigration: !!cap.isImmigration,
        features: { ...DEFAULT_FEATURES, ...(cap.features || {}) },
        content: { ...DEFAULT_CONTENT, ...(cap.content || {}) },
        widgetConfig: { ...DEFAULT_WIDGET_CONFIG, ...(res?.data?.widgetConfig || {}) },
        refresh: load,
      });
    } catch (err) {
      // Unauthenticated / onboarding / transient error → neutral default (no tools).
      setState((prev) => ({ ...prev, loading: false, refresh: load }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [apiClient]);

  useEffect(() => { load(); }, [load]);

  return (
    <CapabilityContext.Provider value={state}>
      {children}
    </CapabilityContext.Provider>
  );
};

export const useCapabilities = () => useContext(CapabilityContext);

/** What the expert's public widget offers (assessment templates, flow switches). */
export const useWidgetConfig = () => useContext(CapabilityContext).widgetConfig;

/** Convenience: read a single feature flag. */
export const useFeature = (name) => {
  const { features } = useContext(CapabilityContext);
  return !!features[name];
};

export default CapabilityContext;
